/** `body.treads` — a top-down tread-laying hull: left/right pivot it in place
 * at a constant turn rate, up drives it forward along its heading and down
 * backs it up, at constant speeds (no momentum). The heading is the entity's
 * attitude (`angle`, clockwise from the art's upright pose, so the art is
 * drawn nose-up). It stops against the arena's edges, solid surfaces and
 * other bodies carrying its `blockTag`, each axis on its own (so it slides
 * along a wall); a push (the push protocol: a conveyor, a current) carries it
 * on top of its drive, through the same stops. A weapon that swings its own aim with B + left/right (the
 * aim protocol below) takes left/right from it while it swings.
 *
 * The AIM PROTOCOL (published by a weapon in its component data, read here
 * and by `body.mount`): `aim` — the weapon's bearing in radians, clockwise
 * from up; `aimRate` — how fast B + left/right swings it (0: the weapon is
 * fixed to the hull); `aiming` — true on a tick B + left/right swings it. */
import { int, id, num, obj } from '../schemaBuilders.js';
import { defineModule, type ModuleCtx } from '../module.js';
import { heldBy, pushVelocity, speedMultiplier } from '../physics.js';
import { overlaps, type EntityV1, type PlayerInputV1 } from '../world.js';

export type TreadsConfig = {
  /** Forward speed (world units per second). */
  speed: number;
  /** Reverse speed (world units per second). */
  reverse: number;
  /** Pivot rate (radians per second) while left/right is held. */
  turnRate: number;
  /** Heading (degrees, clockwise from up) a hull spawned facing right takes;
   * one spawned facing left takes its mirror image. */
  startHeading: number;
  /** Other bodies this one cannot drive through (each carries the tag). */
  blockTag: string;
  /** Ticks between two `engine` cues while it keeps driving. */
  engineCueTicks: number;
};

const IDLE: PlayerInputV1 = { dx: 0, dy: 0, a: false, b: false, p: null };
const TAU = Math.PI * 2;
/** The distance a steered hull closes to before it stops and shoots. */
const FIRING_RANGE = 4;
/** How far off its flank a sidestep affordance lies, and how weakly it pulls. */
const SIDESTEP = 2.5, SIDESTEP_WEIGHT = 0.3;

/** An angle wrapped into (−π, π] (never −0: worlds compare by `Object.is`). */
export function wrapAngle(a: number): number {
  const r = a - TAU * Math.floor((a + Math.PI) / TAU);
  return r === -Math.PI ? Math.PI : r + 0;
}

/** The unit vector of a bearing (clockwise from up; world y grows down). */
export function bearingVector(a: number): { x: number; y: number } {
  return { x: Math.sin(a), y: -Math.cos(a) };
}

/** The bearing from one point to another (clockwise from up). */
export function bearingTo(from: { x: number; y: number }, to: { x: number; y: number }): number {
  return Math.atan2(to.x - from.x, from.y - to.y);
}

/** An entity's box centre (the composed y is its bottom edge). */
export function centreOf(e: EntityV1): { x: number; y: number } {
  return { x: e.x, y: e.y - e.h / 2 };
}

/** The weapon on `e` that publishes the aim protocol, if any. */
export function aimOf(e: EntityV1): { aim: number; aimRate: number; aiming: boolean } | null {
  for (const key of Object.keys(e.c).sort()) {
    const d = e.c[key]!;
    if (typeof d.aim === 'number') return { aim: d.aim, aimRate: typeof d.aimRate === 'number' ? d.aimRate : 0, aiming: d.aiming === true };
  }
  return null;
}

/** The heading a hull spawned with `facing` starts a round on. */
export function startHeadingOf(c: TreadsConfig, facing: 1 | -1): number {
  return wrapAngle(((facing === -1 ? -c.startHeading : c.startHeading) * Math.PI) / 180);
}

/** Move one axis by `delta`, stopping flush against the arena edge or the
 * first blocker entered (a solid surface, another `blockTag` body); a body
 * already overlapping something it would push further into stays put. */
function slide(ctx: ModuleCtx<TreadsConfig>, e: EntityV1, axis: 'x' | 'y', delta: number): void {
  if (delta === 0) return;
  const { w, h } = ctx.world.bounds;
  const blockers = ctx.entities({ status: 'active' }).filter(o => o.id !== e.id && (o.tags.includes('surface') || o.tags.includes(ctx.config.blockTag)));
  const before = blockers.filter(o => overlaps(ctx.world, e, o, 0.001));
  const from = e[axis];
  e[axis] = from + delta;
  // The arena edge: flush against it (x is the centre, y the bottom edge).
  if (axis === 'x') e.x = Math.min(w - e.w / 2, Math.max(e.w / 2, e.x));
  else e.y = Math.min(h, Math.max(e.h, e.y));
  for (const o of blockers) {
    if (!overlaps(ctx.world, e, o, 0.001)) continue;
    if (before.includes(o)) { e[axis] = from; return; }
    // Flush against the face it entered.
    if (axis === 'x') e.x = delta > 0 ? Math.min(e.x, o.x - (o.w + e.w) / 2) : Math.max(e.x, o.x + (o.w + e.w) / 2);
    else e.y = delta > 0 ? Math.min(e.y, o.y - o.h) : Math.max(e.y, o.y + e.h);
  }
}

export const treads = defineModule<TreadsConfig>({
  id: 'body.treads', version: 1, attach: 'entity', role: 'movement',
  summary: 'Top-down tank hull: left/right pivot it in place, up drives forward along its heading and down backs up, at constant speeds; stops against the arena edges, solid surfaces and other blocking bodies (sliding along them); yields left/right to a weapon swinging its aim with B.',
  config: obj({ speed: num(0.5, 30), reverse: num(0, 30), turnRate: num(0.2, 15), startHeading: num(-180, 180), blockTag: id(), engineCueTicks: int(1, 300) }),
  requires: [], events: ['engine'], presentation: { engine: { cue: 'dash' } },
  proofs: ['pivots-in-place', 'drives-along-heading', 'reverses', 'mirrored-start-heading', 'stops-at-arena-edge', 'slides-along-a-wall',
    'blocked-by-another-hull', 'push-protocol', 'yields-turn-to-a-swinging-weapon', 'round-start-restores-heading', 'controller-attacks-a-target', 'controller-backs-up-to-a-point-behind',
    'publishes-flank-sidesteps'],
  animStates: () => ['idle', 'move'],
  init(ctx, e) {
    e.angle = startHeadingOf(ctx.config, e.facing);
    Object.assign(ctx.data(e), { driving: false, cue: 0 });
  },
  systems: {
    move(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      if (typeof d.cue === 'number' && d.cue > 0) d.cue -= 1;
      if (e.status !== 'active' || heldBy(e)) {
        d.driving = false;
        // Frozen (a hurt pose, a round break): parked; a rule's pose overrides it.
        if (e.status === 'frozen') ctx.setAnim(e, 'idle');
        return;
      }
      const input = e.slot === null ? IDLE : ctx.input(e.slot);
      const turn = aimOf(e)?.aiming ? 0 : input.dx;
      e.angle = wrapAngle(e.angle + turn * c.turnRate * ctx.dt);
      const throttle = -input.dy;
      const speed = (throttle > 0 ? c.speed : c.reverse) * speedMultiplier(e) * throttle;
      const v = bearingVector(e.angle);
      e.vx = v.x * speed; e.vy = v.y * speed;
      // Each axis on its own: a hull driven into a wall slides along it.
      const push = pushVelocity(ctx.signals('push'), e);
      const x = e.x, y = e.y, sx = (e.vx + push.vx) * ctx.dt, sy = (e.vy + push.vy) * ctx.dt;
      slide(ctx, e, 'x', sx);
      if (e.x !== x + sx) e.vx = 0;
      slide(ctx, e, 'y', sy);
      if (e.y !== y + sy) e.vy = 0;
      e.grounded = false; e.groundId = null;
      const driving = throttle !== 0 || turn !== 0;
      if (driving && d.driving !== true && d.cue === 0) {
        ctx.emit('engine', { x: e.x, y: e.y - e.h / 2, slot: e.slot ?? 0 });
        d.cue = c.engineCueTicks;
      }
      d.driving = driving;
      // Set at move, so a later stage's pose (a shot, a hurt, a win) overrides it.
      ctx.setAnim(e, driving ? 'move' : 'idle');
    },
    present(ctx, e) {
      // A new round (flow.rounds) puts the hull back on its start heading.
      if (e && ctx.signals('round-start').some(s => s.target === e.id)) { e.angle = startHeadingOf(ctx.config, e.facing); ctx.data(e).driving = false; }
    },
  },
  // Sidesteps: a point off each flank of the hull, so a hull with a shell
  // coming at it has somewhere to go. They pull weakly: a rival to attack
  // (weight 3) always outpulls them anywhere in a 64-unit world, so the
  // attack stays among the solver's candidates and a sidestep wins only
  // when the attack's own rollout goes worse.
  solverTargets(ctx, e, player) {
    if (!e || e.id !== player.id || e.status !== 'active') return [];
    const at = centreOf(e), { w, h } = ctx.world.bounds;
    return [Math.PI / 2, -Math.PI / 2].map(turn => {
      const v = bearingVector(e.angle + turn);
      return { x: Math.min(w - e.w, Math.max(e.w, at.x + v.x * SIDESTEP)), y: Math.min(h - e.h, Math.max(e.h, at.y + v.y * SIDESTEP)),
        weight: SIDESTEP_WEIGHT, label: 'sidestep' };
    });
  },
  /** Drive to `goal`. A pressed goal is a TARGET to shoot: swing the weapon
   * onto it (B + left/right when the weapon aims on its own), and close in
   * with the hull pointed at it until the weapon bears on it inside firing
   * range, holding the primary button while it does. An unpressed goal is a
   * point to drive to (backing up to one behind) and stop at. */
  steer(ctx, e, goal) {
    if (e.status !== 'active') return IDLE;
    const c = ctx.config;
    const at = centreOf(e), dist = Math.hypot(goal.x - at.x, goal.y - at.y);
    const bearing = bearingTo(at, goal);
    const hullErr = wrapAngle(bearing - e.angle);
    const step = c.turnRate * ctx.dt;
    const turnTo = (err: number): -1 | 0 | 1 => (Math.abs(err) <= step / 2 ? 0 : err > 0 ? 1 : -1);
    if (!goal.press) {
      if (dist < 0.3) return IDLE;
      // Behind: back up to it (the tail turned onto it); else drive to it.
      if (Math.abs(hullErr) > 2.2) return { dx: turnTo(wrapAngle(hullErr + Math.PI)), dy: 1, a: false, b: false, p: null };
      return { dx: turnTo(hullErr), dy: Math.abs(hullErr) < 0.6 ? -1 : 0, a: false, b: false, p: null };
    }
    const weapon = aimOf(e);
    const swings = weapon !== null && weapon.aimRate > 0;
    const aimErr = wrapAngle(bearing - (weapon ? weapon.aim : e.angle));
    // Held directions turn the aim a whole step a tick: it is as fine as it
    // gets within half a step (a shot from there may still miss).
    const grain = (swings ? weapon.aimRate : c.turnRate) * ctx.dt / 2;
    const approach: -1 | 0 = Math.abs(hullErr) < 0.6 ? -1 : 0;
    if (swings && Math.abs(aimErr) > grain) {
      return { dx: aimErr > 0 ? 1 : -1, dy: dist > FIRING_RANGE ? approach : 0, a: false, b: true, p: null };
    }
    const onTarget = Math.abs(aimErr) <= grain;
    // Inside firing range with the aim on: hold ground and shoot; else close in.
    const hold = onTarget && dist <= FIRING_RANGE;
    return { dx: hold && swings ? 0 : turnTo(hullErr), dy: hold ? 0 : approach, a: onTarget, b: false, p: null };
  },
});

